import React, { useState, useEffect } from 'react';
import { PricingConfiguration, PricingConfigurationCreateData } from '../../services/billingService';
import { useAppSelector } from '../../store/hooks';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { DollarSign, Save, X } from 'lucide-react';

interface PricingConfigurationFormProps {
  pricing?: PricingConfiguration;
  onSubmit: (data: PricingConfigurationCreateData) => void;
  onCancel: () => void;
  loading?: boolean;
  serverErrors?: Record<string, string>;
}

const PricingConfigurationForm: React.FC<PricingConfigurationFormProps> = ({
  pricing,
  onSubmit,
  onCancel,
  loading = false,
  serverErrors = {}
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [formData, setFormData] = useState<PricingConfigurationCreateData>({
    name: '',
    setup_fee: '150.00',
    guest_base_fee: '2.00',
    companion_fee: '1.00',
    image_message_fee: '0.50',
    save_the_date_fee: '0.50',
    usher_service_fee: '75.00',
    is_active: true,
    notes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when editing
  useEffect(() => {
    if (pricing) {
      setFormData({
        name: pricing.name,
        setup_fee: pricing.setup_fee,
        guest_base_fee: pricing.guest_base_fee,
        companion_fee: pricing.companion_fee,
        image_message_fee: pricing.image_message_fee,
        save_the_date_fee: pricing.save_the_date_fee,
        usher_service_fee: pricing.usher_service_fee || '75.00',
        is_active: pricing.is_active,
        notes: pricing.notes || ''
      });
    }
  }, [pricing]);

  // Merge server errors with local errors
  const allErrors = { ...errors, ...serverErrors };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    const validateDecimal = (value: string, fieldName: string, min: number = 0) => {
      const num = parseFloat(value);
      if (isNaN(num) || num < min) {
        newErrors[fieldName] = `Must be a number greater than or equal to ${min}`;
      }
    };

    validateDecimal(formData.setup_fee, 'setup_fee', 0);
    validateDecimal(formData.guest_base_fee, 'guest_base_fee', 0);
    validateDecimal(formData.companion_fee, 'companion_fee', 0);
    validateDecimal(formData.image_message_fee, 'image_message_fee', 0);
    validateDecimal(formData.save_the_date_fee, 'save_the_date_fee', 0);
    validateDecimal(formData.usher_service_fee, 'usher_service_fee', 0);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleInputChange}
            error={allErrors.name}
            placeholder="e.g., 2025 Pricing"
            label="Configuration Name *"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              id="setup_fee"
              name="setup_fee"
              type="number"
              step="0.01"
              min="0"
              value={formData.setup_fee}
              onChange={handleInputChange}
              error={allErrors.setup_fee}
              placeholder="150.00"
              label="Setup Fee (JOD) *"
              leftIcon={<DollarSign className="h-4 w-4" />}
              required
            />
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              One-time fee for first active event
            </p>
          </div>

          <div>
            <Input
              id="guest_base_fee"
              name="guest_base_fee"
              type="number"
              step="0.01"
              min="0"
              value={formData.guest_base_fee}
              onChange={handleInputChange}
              error={allErrors.guest_base_fee}
              placeholder="2.00"
              label="Guest Base Fee (JOD) *"
              leftIcon={<DollarSign className="h-4 w-4" />}
              required
            />
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Per guest fee (includes invitation + reminders)
            </p>
          </div>

          <div>
            <Input
              id="companion_fee"
              name="companion_fee"
              type="number"
              step="0.01"
              min="0"
              value={formData.companion_fee}
              onChange={handleInputChange}
              error={allErrors.companion_fee}
              placeholder="1.00"
              label="Companion Fee (JOD) *"
              leftIcon={<DollarSign className="h-4 w-4" />}
              required
            />
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Per companion fee
            </p>
          </div>

          <div>
            <Input
              id="image_message_fee"
              name="image_message_fee"
              type="number"
              step="0.01"
              min="0"
              value={formData.image_message_fee}
              onChange={handleInputChange}
              error={allErrors.image_message_fee}
              placeholder="0.50"
              label="Image Message Fee (JOD) *"
              leftIcon={<DollarSign className="h-4 w-4" />}
              required
            />
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Per image message sent
            </p>
          </div>

          <div>
            <Input
              id="save_the_date_fee"
              name="save_the_date_fee"
              type="number"
              step="0.01"
              min="0"
              value={formData.save_the_date_fee}
              onChange={handleInputChange}
              error={allErrors.save_the_date_fee}
              placeholder="0.50"
              label="Save the Date Fee (JOD) *"
              leftIcon={<DollarSign className="h-4 w-4" />}
              required
            />
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Per "Save the Date" message sent
            </p>
          </div>

          <div>
            <Input
              id="usher_service_fee"
              name="usher_service_fee"
              type="number"
              step="0.01"
              min="0"
              value={formData.usher_service_fee}
              onChange={handleInputChange}
              error={allErrors.usher_service_fee}
              placeholder="0.00"
              label="Usher Service Fee (JOD) *"
              leftIcon={<DollarSign className="h-4 w-4" />}
              required
            />
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              One-time fee per event for enabling usher service
            </p>
          </div>
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Notes (Optional)
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            className={`w-full px-4 py-2.5 rounded-md border-2 transition-colors ${
              theme === 'dark'
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
            }`}
            placeholder="Additional notes about this pricing configuration..."
          />
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          leftIcon={<X className="h-4 w-4" />}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          leftIcon={<Save className="h-4 w-4" />}
        >
          {pricing ? 'Update' : 'Create'} Configuration
        </Button>
      </div>
    </form>
  );
};

export default PricingConfigurationForm;

